import React, { useEffect, useState } from 'react';

import Button from '../../../Button/Button';
import type { OnSuggestionsFeedbackVoteFn } from '../../utils/types';

export type SuggestionsSearchFeedbackProps = {
  isActive: boolean;
  isResponsive?: boolean;
  onVote?: OnSuggestionsFeedbackVoteFn;
};

function normalizeAcknowledgment(value: unknown): string | undefined {
  if (typeof value !== 'string') {
    return undefined;
  }
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : undefined;
}

const PROMPT = 'Is this response useful?';
const DEFAULT_THANKS = 'Thanks for your feedback!';
const LABEL_YES = 'Yes, useful';
const LABEL_NO = 'Not useful';

const SuggestionsSearchFeedback: React.FC<SuggestionsSearchFeedbackProps> = ({
  isActive,
  isResponsive,
  onVote,
}) => {
  const [submitted, setSubmitted] = useState(false);
  const [acknowledgmentText, setAcknowledgmentText] = useState<string | undefined>(undefined);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (!isActive) {
      setSubmitted(false);
      setAcknowledgmentText(undefined);
      setIsSubmitting(false);
    }
  }, [isActive]);

  if (!isActive) {
    return null;
  }

  const displayText = acknowledgmentText ?? DEFAULT_THANKS;
  /** Align with DAM AI thumbs: small hit target + `onlyIconHoverColor` → `Button--icon-hover-primary`. */
  const buttonSize = 'small';
  const rootClass = `SuggestionsSearchFeedback${isResponsive ? ' SuggestionsSearchFeedback--responsive' : ''}`;

  const handleVote = async (useful: boolean) => {
    if (isSubmitting) {
      return;
    }
    setIsSubmitting(true);
    let resolved: unknown;
    try {
      resolved = await Promise.resolve(onVote?.(useful));
    } catch {
      resolved = undefined;
    }
    setAcknowledgmentText(normalizeAcknowledgment(resolved));
    setIsSubmitting(false);
    setSubmitted(true);
  };

  if (submitted) {
    return (
      <div className={rootClass} data-testid="suggestions-feedback-thanks">
        <span className="SuggestionsSearchFeedback__text" data-testid="suggestions-feedback-thanks-text">
          {displayText}
        </span>
      </div>
    );
  }

  return (
    <div
      className={rootClass}
      data-testid="suggestions-feedback-prompt"
      aria-busy={isSubmitting}
    >
      <span className="SuggestionsSearchFeedback__text" data-testid="suggestions-feedback-prompt-text">
        {PROMPT}
      </span>
      <div className="SuggestionsSearchFeedback__actions">
        <Button
          type="button"
          aria-label={LABEL_YES}
          onlyIcon
          icon="v2-ThumbsUp"
          version="v2"
          buttonType="tertiary"
          size={buttonSize}
          onlyIconHoverColor="primary"
          testId="am-v2-suggestions-feedback-yes"
          disabled={isSubmitting}
          onClick={() => handleVote(true)}
        />
        <Button
          type="button"
          aria-label={LABEL_NO}
          onlyIcon
          icon="v2-ThumbsDown"
          version="v2"
          buttonType="tertiary"
          size={buttonSize}
          onlyIconHoverColor="primary"
          testId="am-v2-suggestions-feedback-no"
          disabled={isSubmitting}
          onClick={() => handleVote(false)}
        />
      </div>
    </div>
  );
};

export default SuggestionsSearchFeedback;
